<template>
  <div id="position-search">
    <base-material-alert
      v-if="showAlertMessage && (!isLoading && !entries.length)"
      color="primary"
      icon="mdi-alert"
      class="position-search-alert mt-3 mb-6"
    >
      <div class="text-justify mr-4">
        {{ alertMessage ? alertMessage : $t(`views.common.positionSearch.alert.defaultMessage`) }}
      </div>
      <div class="text-right mr-4">
        <router-link class="white--text" to="/">
          {{ $t('views.common.positionSearch.alert.goToPositions') }}
        </router-link>
      </div>
    </base-material-alert>
    <v-autocomplete
      v-model="myPosition"
      hide-details
      :autofocus="autofocus"
      :items="items"
      :loading="isLoading"
      :disabled="disabled"
      hide-no-data
      :label="
        type && !Array.isArray(type)
          ? $t(`views.common.positionSearch.${type}.position`)
          : $t(`views.common.positionSearch.position`)
      "
      :placeholder="
        type && !Array.isArray(type)
          ? $t(`views.common.positionSearch.${type}.startTyping`)
          : $t(`views.common.positionSearch.startTyping.${searchType}`)
      "
    ></v-autocomplete>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { get, find, sortBy } from 'lodash'
import { getPositionsLean, getWatchesLean } from '@/lib/api.js'
import { getPositionText } from '@/lib/positions/positions.js'
import { isValidObjectId } from '@/lib/utils/mongooseUtils.js'

export default {
  props: {
    value: {
      type: null,
      default: () => {},
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    type: {
      type: null,
      default: null,
    },
    currency: {
      type: null,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showAlertMessage: {
      type: Boolean,
      default: false,
    },
    alertMessage: {
      type: String,
      default: null,
    },
    positions: {
      type: Array,
      default: () => [],
    },
    showBaseCurrencyPosition: {
      type: Boolean,
      default: false,
    },
    searchType: {
      type: String,
      default: 'position',
    },
  },
  data: () => ({
    myPosition: null,
    entries: [],
    isLoading: false,
    search: null,
  }),
  computed: {
    ...mapState(['user']),
    items() {
      const items = this.entries.map((entry) => {
        return {
          text: getPositionText(entry),
          value: entry._id,
        }
      })
      return sortBy(items, ['text'])
    },
  },
  watch: {
    myPosition: {
      async handler() {
        if (!this.myPosition) return
        if (this.myPosition === this.value) return
        if (!this.entries.length) await this.loadPositions()
        this.$emit('input', find(this.entries, { _id: this.myPosition }))
      },
      deep: true,
    },
    value: {
      handler() {
        if (typeof this.value === 'string' && isValidObjectId(this.value))
          this.myPosition = this.value
        else this.myPosition = get(this.value, '_id', null)
      },
      immediate: true,
    },
    type: {
      async handler() {
        await this.loadPositions()
      },
    },
    currency: {
      async handler() {
        await this.loadPositions()
      },
    },
  },
  async mounted() {
    await this.loadPositions()
  },
  methods: {
    async loadPositions() {
      this.isLoading = true
      try {
        const positions =
          this.searchType === 'position'
            ? await getPositionsLean({
                filter: {
                  type: this.type ? this.type : undefined,
                  currency: this.currency ? this.currency : undefined,
                },
              })
            : await getWatchesLean({
                filter: {
                  type: this.type ? this.type : undefined,
                  currency: this.currency ? this.currency : undefined,
                },
              })

        let entries = positions.data
        if (!this.showBaseCurrencyPosition) {
          entries = entries.filter(
            (item) => item.type !== 'cash' || item.ticker !== this.user.baseCurrency
          )
        }
        this.entries = entries
        this.$emit('update:positions', this.entries)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss">
#position-search {
  .position-search-alert {
    .alert-message-link {
      color: white;
      text-decoration: underline;
    }
  }
}
</style>
